import React, { useEffect, useRef, useState, useCallback } from "react";
import { YOUTUBE, FEED_URL, CORS_PROXIES, FALLBACK_VIDEOS } from "../../config/youtube";
import "./videos.css";

/** Parse the YouTube channel RSS feed into a plain array of videos. */
export function parseFeed(xmlText, limit = 12) {
  const doc = new DOMParser().parseFromString(xmlText, "text/xml");
  if (doc.getElementsByTagName("parsererror").length) {
    throw new Error("Malformed feed");
  }

  const entries = Array.from(doc.getElementsByTagName("entry"));
  const videos = entries
    .map((entry) => {
      const get = (tag) => entry.getElementsByTagName(tag)[0]?.textContent || "";
      // yt:videoId is namespaced; fall back to the watch link if it is absent.
      let id = get("yt:videoId") || get("videoId");
      if (!id) {
        const href =
          entry.getElementsByTagName("link")[0]?.getAttribute("href") || "";
        id = (href.match(/[?&]v=([\w-]{11})/) || [])[1] || "";
      }
      if (!id) return null;
      return {
        id,
        title: get("title"),
        published: get("published"),
        url: `https://www.youtube.com/watch?v=${id}`,
        thumbnail: `https://i.ytimg.com/vi/${id}/hqdefault.jpg`,
      };
    })
    .filter(Boolean);

  return videos.slice(0, limit);
}

function formatDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

export default function Videos() {
  const [videos, setVideos] = useState([]);
  const [status, setStatus] = useState("loading"); // loading | ready | failed
  const railRef = useRef(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  // --- fetch ------------------------------------------------------------
  useEffect(() => {
    let cancelled = false;

    (async () => {
      // Try CORS proxies first
      for (const proxy of CORS_PROXIES) {
        try {
          const url = proxy(FEED_URL);
          const res = await fetch(url, {
            headers: { "Accept": "application/xml, text/xml" },
            signal: AbortSignal.timeout(8000), // 8 second timeout
          });
          if (!res.ok) {
            console.warn(`Proxy returned ${res.status}`, url);
            continue;
          }
          const text = await res.text();
          if (!text.trim()) {
            console.warn("Proxy returned empty response", url);
            continue;
          }
          const parsed = parseFeed(text, YOUTUBE.maxVideos);
          if (parsed.length > 0 && !cancelled) {
            setVideos(parsed);
            setStatus("ready");
            return;
          }
        } catch (e) {
          console.warn("Proxy failed:", e.message);
          // Try next proxy
        }
      }

      // All proxies failed - use hardcoded fallback
      if (!cancelled) {
        console.error("All CORS proxies exhausted, using fallback videos");
        setVideos(FALLBACK_VIDEOS);
        setStatus("ready");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  // --- arrow state ------------------------------------------------------
  const updateArrows = useCallback(() => {
    const el = railRef.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 8);
    setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 8);
  }, []);

  useEffect(() => {
    updateArrows();
    const el = railRef.current;
    if (!el) return undefined;
    el.addEventListener("scroll", updateArrows, { passive: true });
    window.addEventListener("resize", updateArrows);
    return () => {
      el.removeEventListener("scroll", updateArrows);
      window.removeEventListener("resize", updateArrows);
    };
  }, [videos, status, updateArrows]);

  const scrollBy = (dir) => {
    const el = railRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * Math.max(el.clientWidth * 0.8, 240), behavior: "smooth" });
  };

  if (status === "loading") {
    return (
      <section className="videos-section" aria-busy="true">
        <div className="videos-head">
          <div>
            <h3>From our YouTube channel</h3>
            <p>Loading our latest videos…</p>
          </div>
        </div>
        <div className="videos-rail videos-rail-static">
          {[0, 1, 2, 3].map((i) => (
            <div className="video-card video-skeleton" key={i}>
              <div className="video-thumb-skel" />
              <div className="video-line-skel" />
              <div className="video-line-skel short" />
            </div>
          ))}
        </div>
      </section>
    );
  }

  if (status === "failed") {
    return (
      <section className="videos-section">
        <div className="videos-head">
          <div>
            <h3>From our YouTube channel</h3>
            <p>Ride reviews, product walkarounds and workshop tips.</p>
          </div>
        </div>
        <div className="videos-fallback">
          <p>
            We couldn't load the latest videos just now. You can watch
            everything directly on our channel.
          </p>
          <a
            className="btn btn-success"
            href={YOUTUBE.channelUrl}
            target="_blank"
            rel="noreferrer"
          >
            Open our YouTube channel
          </a>
        </div>
      </section>
    );
  }

  return (
    <section className="videos-section">
      <div className="videos-head">
        <div>
          <h3>From our YouTube channel</h3>
          <p>Ride reviews, product walkarounds and workshop tips.</p>
        </div>
        <div className="videos-head-actions">
          <a
            className="videos-channel-link"
            href={YOUTUBE.channelUrl}
            target="_blank"
            rel="noreferrer"
          >
            View all
          </a>
          <div className="videos-arrows">
            <button
              type="button"
              aria-label="Scroll videos left"
              onClick={() => scrollBy(-1)}
              disabled={!canLeft}
            >
              ‹
            </button>
            <button
              type="button"
              aria-label="Scroll videos right"
              onClick={() => scrollBy(1)}
              disabled={!canRight}
            >
              ›
            </button>
          </div>
        </div>
      </div>

      <ul className="videos-rail" ref={railRef}>
        {videos.map((v) => (
          <li className="video-card" key={v.id}>
            <a href={v.url} target="_blank" rel="noreferrer">
              <span className="video-thumb">
                {v.thumbnail ? (
                  <img src={v.thumbnail} alt="" loading="lazy" onError={(e) => {
                    e.target.style.display = "none";
                  }} />
                ) : null}
                <span className="video-play" aria-hidden="true">
                  <svg viewBox="0 0 24 24" width="22" height="22">
                    <path d="M8 5v14l11-7z" fill="currentColor" />
                  </svg>
                </span>
              </span>
              <span className="video-title">{v.title}</span>
              <span className="video-date">{formatDate(v.published)}</span>
            </a>
          </li>
        ))}
      </ul>
    </section>
  );
}
